// ── Cross-option mirroring guards ────────────────────────────────────────────
// Mirroring exists for COOKIE-CUTTER multi-option quotes — Gold/Silver/Bronze
// carrying the same base scope — so one edit doesn't have to be typed into
// every option. It must never couple options that are genuinely different
// documents. The Chris Gorrigan Generac quote (2026-08-17) is the failure
// this file exists for: options were ["18kW Generac Standby Package",
// "Add-On — 2 Concrete Pads"], an add of "8c#14" TECK to the package option
// was mirrored into the concrete-pad add-on (adds mirrored unconditionally),
// and deleting that stray copy from the add-on mirrored the REMOVE back and
// silently deleted the line from the package option too.
//
// Four rules, all pure so scripts/test_quote_mirror.js can replay them over
// real drafts:
//   1. optionsShareBaseScope — mirror ONLY between options that visibly share
//      a base scope. An add-on/allowance option shares ~nothing with the main
//      package, so nothing mirrors in either direction.
//   2. isMirrorCloneId — a line whose id carries the mirror-clone prefix got
//      INTO its option by mirroring; deleting it is a local "doesn't belong
//      here" correction and must never propagate the delete outward.
//   3. optionMirrorOutMode / optionMirrorsOut — a freshly DUPLICATED option
//      never mirrors OUT (its edits are the differentiation it was copied
//      for) until the user turns mirroring on for it. Huhn #2656, below.
//   4. mirrorDefaultFor — a quote already pushed to Jobber starts with the
//      mirror OFF: its options are client-facing prices.
import { normDesc } from "./propagatePick.js";

// Ids minted by mirrorPatchAcrossOptions for mirrored ADD clones.
export const isMirrorCloneId = (id) => /^(?:mat|scope|note|exc)_mir_/.test(String(id || ""));

// Normalized identity keys for the lines that define an option's scope.
// Materials + scope lines are the meat; notes/exclusions are boilerplate that
// would inflate similarity between unrelated options.
function scopeKeysOf(draft) {
  const keys = new Set();
  for (const m of draft?.materials || []) {
    const k = normDesc(m?.description);
    if (k) keys.add(`m:${k}`);
  }
  for (const s of draft?.scope || []) {
    const k = normDesc(typeof s === "string" ? s : s?.text);
    if (k) keys.add(`s:${k}`);
  }
  return keys;
}

// Measured over every live multi-option quote (2026-08-17: 42 quotes, 114
// option pairs): every add-on / allowance / per-phase-section pair — the kind
// that must never couple — shares ≤ 0.47 of the smaller option's lines, and
// cookie-cutter siblings sit at 0.50–1.00, so 0.5 is the exact boundary. The
// pairs just under it (0.42–0.47) are related-variant options where a MISSED
// mirror costs one hand edit; a wrong mirror silently rewrites another
// document — so the gate errs high.
export const BASE_SCOPE_SHARE_MIN = 0.5;

export function optionsShareBaseScope(a, b) {
  const ka = scopeKeysOf(a);
  const kb = scopeKeysOf(b);
  if (!ka.size || !kb.size) return false;
  let shared = 0;
  for (const k of ka) if (kb.has(k)) shared += 1;
  return shared / Math.min(ka.size, kb.size) >= BASE_SCOPE_SHARE_MIN;
}

// ── Rule 3: a fresh DUPLICATE never mirrors OUT ──────────────────────────────
// Richard Huhn #2656 (2026-09-15): the 2-option quote had been pushed to Jobber
// and sent. Dylan duplicated an option ("⧉ Duplicate this" → "<label> (copy)")
// to build a cheaper 18kW package with the toggle still on. A duplicate shares
// 100% of its source's scope, so rule 1 saw the perfect sibling — and every
// edit made to the copy (generator swap, ATS qty, cable runs, allowance costs,
// a scope-line delete) mirrored back into BOTH as-sent options: $28,300.56 →
// $24,802.66 and $31,664.76 → $28,166.86 in ElecBid (Jobber untouched; there
// is no re-push). But edits to a fresh copy are, by definition, the
// DIFFERENTIATION it was copied for — never a shared-line fix.
//
// So: an option the editor stamped `copied_from` (or, for copies made before
// the stamp existed, one still labelled "… (copy)") never mirrors OUT until
// the user turns mirroring on for it — `mirror_out: true` on the option,
// persisted with the quote. `mirror_out: false` makes ANY option local-only.
// A guarded option still RECEIVES mirrors: fixing a shared line at the source
// flows into the copy, which is the cookie-cutter case rule 1 exists for.
// The option KEY proves nothing: the editor mints `opt_<random>` for a
// duplicate, but the server mints `opt_1`, `opt_2` for its own options.
export const COPY_LABEL_RE = /\(copy\)\s*$/i;

// "on"   → this option's edits mirror out to its cookie-cutter siblings
// "copy" → a fresh duplicate: edits stay local until mirroring is turned on for it
// "off"  → the user switched mirroring out off for this option
export function optionMirrorOutMode(opt) {
  if (!opt) return "off";
  if (opt.mirror_out === true) return "on";
  if (opt.mirror_out === false) return "off";
  if (opt.copied_from) return "copy";
  const label = opt.label || opt.draft?.meta?.tier_label || "";
  return COPY_LABEL_RE.test(String(label)) ? "copy" : "on";
}
export const optionMirrorsOut = (opt) => optionMirrorOutMode(opt) === "on";

// ── Rule 4: a SENT quote starts with the mirror OFF ──────────────────────────
// Once a quote has been pushed to Jobber its options are client-facing prices;
// an edit that quietly rewrites a sibling is the incident above. The toggle
// stays available — it just starts off. `pushed_to_jobber_at` counts too: a
// later refine can move workflow_state on, the stamp stays.
export const SENT_WORKFLOW_STATES = new Set(["pushed_to_jobber"]);
export function mirrorDefaultFor(quote) {
  if (!quote) return true;
  if (SENT_WORKFLOW_STATES.has(String(quote.workflow_state || ""))) return false;
  if (quote.pushed_to_jobber_at) return false;
  return true;
}
